import {
  OngoingMonitring,
  OngoingStatus,
  OngoingCheckingStatus,
  ApplicationScheme,
} from '../../../common/types';
import { CorporateQuestionnarieFormValues } from '../../../corporate/types';
import {
  NewApplicationStepStatus,
  CorporateFormValues,
  NewApplicationCorporateSteps,
  NewApplicationStatus,
  CofApprovalStatus,
  ApplicationDocument,
  ApplicationDocumentStatus,
  ApplicationAgreement,
  NewApplicationChildrenInfo,
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ExpectedFundsOriginsFormValues,
} from '../../types';

export const getStepOneStatus = (
  values: CorporateFormValues,
  stepStatus?: NewApplicationStepStatus,
  applicationScheme?: ApplicationScheme,
  questionnarieFields?: CorporateQuestionnarieFormValues,
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[],
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[],
  accountsFlowsValues?: AccountsFlowsFormValues[],
) => {
  let stepOneStatus = NewApplicationStepStatus.AWAITING || stepStatus;
  let contactDetailsComplete = true;
  let pepComplete = true;
  let groupOfCompaniesComplete = true;
  let businessActivitiesComplete = true;
  let customerWealthComplete = true;
  let accountsCurrenciesComplete = true;
  let accountsFlowComplete = true;
  let expectedOriginsOfFundsComplete = true;
  let businessLicenseComplete = true;

  if (
    applicationScheme &&
    applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
  ) {
    contactDetailsComplete = false;
    pepComplete = false;
    groupOfCompaniesComplete = false;
    businessActivitiesComplete = false;
    customerWealthComplete = false;
    accountsCurrenciesComplete = false;
    accountsFlowComplete = false;
    expectedOriginsOfFundsComplete = false;
    businessLicenseComplete = false;

    if (
      Boolean(values.requiresBusinessLicense === 'no') ||
      (Boolean(values.requiresBusinessLicense === 'yes') &&
        values.businessLicense)
    ) {
      businessLicenseComplete = true;
    }

    if (
      questionnarieFields?.contactPersonName &&
      questionnarieFields?.phoneNumber &&
      questionnarieFields?.contactEmail
    ) {
      contactDetailsComplete = true;
    }

    if (
      questionnarieFields?.isPepOrAssociate?.toString() !== 'null' &&
      (questionnarieFields?.isPepOrAssociate === 'no' ||
        (questionnarieFields?.isPepOrAssociate === 'yes' &&
          questionnarieFields?.nameOfPep &&
          questionnarieFields?.relToCustomerId &&
          questionnarieFields?.orgEmployerAndPosition &&
          questionnarieFields?.positionHeldCountryId &&
          questionnarieFields?.politicalFunctionPeriodFrom &&
          questionnarieFields?.politicalFunctionPeriodTo &&
          questionnarieFields?.abilityMoveGovernmentFunds?.toString() !==
            'null' &&
          (questionnarieFields?.abilityMoveGovernmentFunds === 'no' ||
            (questionnarieFields?.abilityMoveGovernmentFunds === 'yes' &&
              questionnarieFields?.abilityMoveGovernmentFundsDetails))))
    ) {
      pepComplete = true;
    }

    if (
      questionnarieFields?.belongsToGroup?.toString() !== 'null' &&
      (questionnarieFields?.belongsToGroup === 'no' ||
        (questionnarieFields?.belongsToGroup === 'yes' &&
          questionnarieFields?.parentCompanyName &&
          questionnarieFields?.parentCompanyCountryId &&
          questionnarieFields?.subsidiariesInfo &&
          questionnarieFields?.mainActivities &&
          questionnarieFields?.groupWealthSourceIds?.length &&
          questionnarieFields?.estimatedTotalWealthEuro?.toString()))
    ) {
      groupOfCompaniesComplete = true;
    }

    if (
      questionnarieFields?.customerAppliesKycProcedures?.toString() !==
        'null' &&
      ((questionnarieFields?.hasBusinessActivity?.toString() !== 'null' &&
        !questionnarieFields?.hasBusinessActivity) ||
        (questionnarieFields?.hasBusinessActivity &&
          questionnarieFields?.businessActivitiesIds?.length)) &&
      questionnarieFields?.customersMainClientsIds?.length &&
      questionnarieFields?.salesChannelsIds?.length &&
      questionnarieFields?.numberOfEmployees?.toString() &&
      questionnarieFields?.sourcesOfFundsIds?.length &&
      questionnarieFields?.hasAccountsInOtherFinancialInstitutions?.toString() !==
        'null' &&
      (questionnarieFields?.hasAccountsInOtherFinancialInstitutions === 'no' ||
        (questionnarieFields?.hasAccountsInOtherFinancialInstitutions ===
          'yes' &&
          questionnarieFields?.otherFinancialInstitutions))
    ) {
      businessActivitiesComplete = true;
    }

    let sourcesOfAnnualIncomesPercentage = 0;

    questionnarieFields?.sourcesOfAnnualIncomes?.forEach((item) => {
      sourcesOfAnnualIncomesPercentage += Number(item.percentage);
    });

    if (
      questionnarieFields?.sourceOfWealthIds?.length &&
      questionnarieFields?.estimatedTotalWealth?.toString() &&
      questionnarieFields?.annualIncome?.toString() &&
      questionnarieFields?.sourcesOfAnnualIncomes?.length &&
      sourcesOfAnnualIncomesPercentage === 100
    ) {
      customerWealthComplete = true;
    }

    if (
      questionnarieFields?.accountTypeIds?.length &&
      accountsCurrenciesValues?.filter(
        (item) =>
          item.currencyId &&
          item.accountOpeningPurpose &&
          item.plannedTransactionNature,
      )?.length
    ) {
      accountsCurrenciesComplete = true;
    }

    if (
      accountsFlowsValues?.filter(
        (item) =>
          item?.expectedAnnualCreditTurnover?.toString() &&
          item?.maxTransferAmountPerTransaction?.toString() &&
          item?.transactionFrequency?.toString() &&
          item?.transactionFrequencyPeriodMonths?.toString(),
      )?.length
    ) {
      accountsFlowComplete = true;
    }

    if (
      expectedFundsOriginsValues?.filter(
        (item) =>
          item?.mainCounterpartiesAndWebsites &&
          item?.countryOfIncorporationId &&
          item?.counterpartyBankCountryIds?.length &&
          item?.activityCountryIds?.length &&
          item?.mainActivities &&
          item?.transactionReason,
      )?.length
    ) {
      expectedOriginsOfFundsComplete = true;
    }
  }

  const businessCompleted =
    values.businessAddressSameAsRegisteredAddress ||
    (values.businessCountryId &&
      values.businessCity &&
      values.businessPostalCode &&
      values.businessAddress1);

  const correspondenceCompleted =
    values.correspondenceAddressSameAsRegisteredAddress ||
    (values.correspondenceCountryId &&
      values.correspondenceCity &&
      values.correspondencePostalCode &&
      values.correspondenceAddress1);

  const vatCompleted =
    !applicationScheme?.vatNumberRequired ||
    (applicationScheme?.vatNumberRequired && values.vatNumber);

  const purposeOfAccountCompleted =
    !applicationScheme?.purposeOfAccountRequired ||
    (applicationScheme?.purposeOfAccountRequired && values.purposeOfAccount);

  const expectedTypesOfClientsCompleted =
    !applicationScheme?.expectedTypeOfClientsRequired ||
    (applicationScheme?.expectedTypeOfClientsRequired &&
      values.expectedTypeOfClients &&
      values.expectedTypeOfClients?.length);

  const requiredCurrencyCompleted =
    !applicationScheme?.requiredCurrenciesCryptoRequired ||
    (applicationScheme?.requiredCurrenciesCryptoRequired &&
      values.requiredCurrenciesCryptoId &&
      values.requiredCurrenciesCryptoId?.length);

  const amlCtfComplianceProgramCompleted =
    !applicationScheme?.amlCtfComplianceProgramRequired ||
    (applicationScheme?.amlCtfComplianceProgramRequired &&
      values.amlCtfComplianceProgram !== null);

  const countriesOfInterestCompleted =
    !applicationScheme?.countriesOfInterestActive ||
    (applicationScheme?.countriesOfInterestActive &&
      values?.countriesOfInterestIds &&
      values?.countriesOfInterestIds?.length > 0) ||
    (!applicationScheme &&
      values?.countriesOfInterestIds &&
      values.countriesOfInterestIds?.length > 0) ||
    (applicationScheme &&
      applicationScheme?.countriesOfInterestActive === null &&
      values?.countriesOfInterestIds &&
      values?.countriesOfInterestIds?.length > 0);

  const stepOneCompleted = Boolean(
    values.countryOfIncorporationId &&
    values.companyName &&
    values.registeredAddress1 &&
    values.registeredCountryId &&
    values.registeredCity &&
    values.registeredPostalCode &&
    businessCompleted &&
    correspondenceCompleted &&
    values.dateOfIncorporation &&
    values.companyNumber &&
    values.websiteLink &&
    values.deliveryChannelId &&
    vatCompleted &&
    purposeOfAccountCompleted &&
    expectedTypesOfClientsCompleted &&
    requiredCurrencyCompleted &&
    amlCtfComplianceProgramCompleted &&
    countriesOfInterestCompleted &&
    contactDetailsComplete &&
    pepComplete &&
    groupOfCompaniesComplete &&
    businessActivitiesComplete &&
    customerWealthComplete &&
    accountsCurrenciesComplete &&
    accountsFlowComplete &&
    expectedOriginsOfFundsComplete &&
    businessLicenseComplete,
  );

  const stepOne: Partial<CorporateFormValues> = {
    countryOfIncorporationId: values.countryOfIncorporationId,
    companyName: values.companyName,
    registeredAddress1: values.registeredAddress1,
    registeredAddress2: values.registeredAddress2,
    registeredCountryId: values.registeredCountryId,
    registeredCity: values.registeredCity,
    registeredPostalCode: values.registeredPostalCode,
    businessAddressSameAsRegisteredAddress:
      values.businessAddressSameAsRegisteredAddress,
    businessCountryId: values.businessCountryId,
    businessCity: values.businessCity,
    businessPostalCode: values.businessPostalCode,
    businessAddress1: values.businessAddress1,
    businessAddress2: values.businessAddress2,
    correspondenceAddressSameAsRegisteredAddress:
      values.correspondenceAddressSameAsRegisteredAddress,
    correspondenceCountryId: values.correspondenceCountryId,
    correspondenceCity: values.correspondenceCity,
    correspondencePostalCode: values.correspondencePostalCode,
    correspondenceAddress1: values.correspondenceAddress1,
    correspondenceAddress2: values.correspondenceAddress2,
    dateOfIncorporation: values.dateOfIncorporation,
    companyNumber: values.companyNumber,
    websiteLink: values.websiteLink,
    countriesOfInterestIds: values.countriesOfInterestIds,
    sicCodes: values.sicCodes,
    businessLicense: values.businessLicense,
    businessDescription: values.businessDescription,
    deliveryChannelId: values.deliveryChannelId,
  };

  if (values.vatNumber) {
    stepOne.vatNumber = values.vatNumber;
  }

  if (stepOneCompleted) {
    stepOneStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepOne).filter(
      (key) => stepOne[key as keyof CorporateFormValues],
    ).length
  ) {
    stepOneStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepOneStatus;
};

export const getStepTwoStatus = (
  values: CorporateFormValues,
  stepStatus?: NewApplicationStepStatus,
) => {
  let stepTwoStatus = NewApplicationStepStatus.AWAITING || stepStatus;

  let stepTwoCompleted = Boolean(
    !values.suppliers?.length && !values.customers?.length,
  );

  if (
    !values.customers?.length &&
    values.suppliers &&
    values.suppliers?.length > 0
  ) {
    stepTwoCompleted = Boolean(
      !values.customers?.length &&
      values.suppliers.filter(
        (item) => item.monthlyTurnoverRangeId && item.name,
      )?.length === values.suppliers?.length,
    );
  } else if (
    !values.suppliers?.length &&
    values.customers &&
    values.customers?.length > 0
  ) {
    stepTwoCompleted = Boolean(
      !values.suppliers?.length &&
      values.customers.filter(
        (item) => item.monthlyTurnoverRangeId && item.name,
      )?.length === values.customers?.length,
    );
  } else if (values.customers?.length && values.suppliers?.length) {
    stepTwoCompleted = Boolean(
      values.customers.filter(
        (item) => item.monthlyTurnoverRangeId && item.name,
      )?.length === values.customers?.length &&
      values.suppliers.filter(
        (item) => item.monthlyTurnoverRangeId && item.name,
      )?.length === values.suppliers?.length,
    );
  }

  const stepTwo: Partial<CorporateFormValues> = {};

  if (values.suppliers && values.suppliers?.length > 0) {
    stepTwo.suppliers = values.suppliers;
  }

  if (values.customers && values.customers?.length > 0) {
    stepTwo.customers = values.customers;
  }

  if (stepTwoCompleted) {
    stepTwoStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepTwo).filter(
      (key) => stepTwo[key as keyof CorporateFormValues],
    ).length
  ) {
    stepTwoStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepTwoStatus;
};

export const getStepThreeStatus = (
  values: CorporateFormValues,
  stepStatus?: NewApplicationStepStatus,
) => {
  let stepThreeStatus = NewApplicationStepStatus.AWAITING || stepStatus;

  const stepThreeСompleted = Boolean(
    values.monthlyTurnoverRangeId &&
    values.monthlyNumberIncomingTransactionsRangeId &&
    values.monthlyNumberOutgoingTransactionsRangeId &&
    values.monthlyValueIncomingTransactionsRangeId &&
    values.monthlyValueOutgoingTransactionsRangeId &&
    values.monthlyMaxValueIncomingPaymentRangeId &&
    values.monthlyMaxValueOutgoingPaymentRangeId &&
    values.productIds &&
    values.productIds.length > 0,
  );

  const stepThree: Partial<CorporateFormValues> = {
    monthlyTurnoverRangeId: values.monthlyTurnoverRangeId,
    monthlyNumberIncomingTransactionsRangeId:
      values.monthlyNumberIncomingTransactionsRangeId,
    monthlyNumberOutgoingTransactionsRangeId:
      values.monthlyNumberOutgoingTransactionsRangeId,
    monthlyValueIncomingTransactionsRangeId:
      values.monthlyValueIncomingTransactionsRangeId,
    monthlyValueOutgoingTransactionsRangeId:
      values.monthlyValueOutgoingTransactionsRangeId,
    monthlyMaxValueIncomingPaymentRangeId:
      values.monthlyMaxValueIncomingPaymentRangeId,
    monthlyMaxValueOutgoingPaymentRangeId:
      values.monthlyMaxValueOutgoingPaymentRangeId,
    productIds: values.productIds,
  };

  if (stepThreeСompleted) {
    stepThreeStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepThree).filter(
      (key) => stepThree[key as keyof CorporateFormValues],
    ).length
  ) {
    stepThreeStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepThreeStatus;
};

export const getStepFourStatus = (
  mainRepresentativeApplications?: NewApplicationChildrenInfo[],
  stepStatus?: NewApplicationStepStatus,
) => {
  let stepFourStatus = NewApplicationStepStatus.AWAITING || stepStatus;

  const acceptedMainRepresentative = mainRepresentativeApplications?.filter(
    (item) => item.subApplication?.status === NewApplicationStatus.ACCEPTED,
  );

  const stepFourСompleted = Boolean(
    acceptedMainRepresentative &&
    acceptedMainRepresentative?.length > 0 &&
    acceptedMainRepresentative.filter(
      (item) => item.status === CofApprovalStatus.ACCEPTED,
    ).length === 1 &&
    mainRepresentativeApplications?.filter(
      (item) => item.status === CofApprovalStatus.INPROCESS || !item.status,
    ).length === 0,
  );

  const stepFive: Partial<CorporateFormValues> = {};

  if (
    mainRepresentativeApplications &&
    mainRepresentativeApplications.length > 0
  ) {
    stepFive.mainRepresentative = mainRepresentativeApplications;
  }

  if (stepFourСompleted) {
    stepFourStatus = NewApplicationStepStatus.COMPLETED;
  } else if (
    Object.keys(stepFive).filter(
      (key) => stepFive[key as keyof CorporateFormValues],
    ).length
  ) {
    stepFourStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  return stepFourStatus;
};

export const getStepFiveStatus = (
  companyOfficials?: NewApplicationChildrenInfo[],
  stepStatus?: NewApplicationStepStatus,
) => {
  let stepFiveStatus = NewApplicationStepStatus.COMPLETED || stepStatus;
  let stepFiveСompleted;

  if (companyOfficials && companyOfficials.length) {
    stepFiveStatus = NewApplicationStepStatus.IN_PROGRESS;

    const acceptedCompanyOfficial = companyOfficials.filter(
      (item) => item.subApplication?.status === NewApplicationStatus.ACCEPTED,
    );

    if (acceptedCompanyOfficial.length) {
      stepFiveСompleted = Boolean(
        (acceptedCompanyOfficial &&
          acceptedCompanyOfficial?.length > 0 &&
          acceptedCompanyOfficial.filter(
            (item) => item.status === CofApprovalStatus.ACCEPTED,
          ).length ===
            companyOfficials.filter(
              (item) =>
                item.status !== CofApprovalStatus.INPROCESS &&
                item.status !== CofApprovalStatus.DECLINED,
            ).length &&
          companyOfficials.filter(
            (item) =>
              item.status === CofApprovalStatus.INPROCESS || !item.status,
          ).length === 0) ||
        companyOfficials.filter(
          (item) => item.status === CofApprovalStatus.DECLINED,
        ).length === companyOfficials.length,
      );
    } else {
      stepFiveСompleted = Boolean(
        companyOfficials.filter(
          (item) => item.status === CofApprovalStatus.INPROCESS || !item.status,
        ).length === 0,
      );
    }

    const stepSix: Partial<CorporateFormValues> = {};

    if (companyOfficials && companyOfficials?.length > 0) {
      stepSix.companyOfficial = companyOfficials;
    }

    if (stepFiveСompleted) {
      stepFiveStatus = NewApplicationStepStatus.COMPLETED;
    } else if (
      Object.keys(stepSix).filter(
        (key) => stepSix[key as keyof CorporateFormValues],
      ).length
    ) {
      stepFiveStatus = NewApplicationStepStatus.IN_PROGRESS;
    }
  }

  return stepFiveStatus;
};

export const getStepSixStatus = (
  documents?: ApplicationDocument[],
  documentCategories?: { id: string; required: boolean; category?: string }[],
  stepStatus?: NewApplicationStepStatus,
) => {
  let stepSixStatus = NewApplicationStepStatus.AWAITING || stepStatus;

  const requiredDocumentsCategories = documentCategories?.filter(
    (item) => item.required,
  );

  if (requiredDocumentsCategories?.length) {
    const filledCategories: string[] = [];
    const emptyCategories: string[] = [];

    if (documents?.length) {
      stepSixStatus = NewApplicationStepStatus.IN_PROGRESS;
    }

    requiredDocumentsCategories?.forEach((item) => {
      if (
        documents?.filter(
          (doc) =>
            (doc.documentCategoryId === item.id ||
              doc.category === item?.category) &&
            doc.status !== 'REJECTED',
        )?.length
      ) {
        if (!filledCategories.find((category) => category === item.id)) {
          filledCategories.push(item.id);
        }
      } else {
        emptyCategories.push(item.id);
      }
    });

    if (!emptyCategories.length && filledCategories.length) {
      const filledCategoriesDocuments: ApplicationDocument[] = [];

      filledCategories?.forEach((id) => {
        const currentCategoryDocuments = documents?.filter(
          (document) => document?.documentCategoryId === id,
        );

        if (currentCategoryDocuments?.length) {
          currentCategoryDocuments?.forEach((item) => {
            if (item.status !== 'REJECTED') {
              filledCategoriesDocuments.push(item);
            }
          });
        }
      });

      if (
        !filledCategoriesDocuments?.filter(
          (item) => item.status !== ApplicationDocumentStatus.ACCEPTED,
        ).length
      ) {
        stepSixStatus = NewApplicationStepStatus.COMPLETED;
      }
    } else {
      stepSixStatus = NewApplicationStepStatus.AWAITING;

      if (documents?.length) {
        stepSixStatus = NewApplicationStepStatus.IN_PROGRESS;
      }
    }
  } else {
    stepSixStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepSixStatus;
};

export const getStepSevenStatus = (
  agreements?: ApplicationAgreement[],
  stepsStatus?: NewApplicationStepStatus,
  agreementsActive?: boolean | null,
  agreementsRequired?: boolean | null,
) => {
  let stepSevenStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const unsignedAgreements: ApplicationAgreement[] = [];

  agreements?.forEach((item) => {
    if (!item.signatures.edges.length) {
      unsignedAgreements.push(item);
    }
  });

  let agreementsCompleted = false;

  if (agreementsActive && !agreementsRequired) {
    agreementsCompleted = true;
  } else if (
    ((agreementsActive === null || agreementsActive === undefined) &&
      !unsignedAgreements.length &&
      agreements) ||
    (agreementsRequired && !unsignedAgreements.length && agreements)
  ) {
    agreementsCompleted = true;
  } else if (
    agreementsActive !== null &&
    agreementsActive !== undefined &&
    !agreementsActive
  ) {
    agreementsCompleted = true;
  }

  if (agreementsCompleted) {
    stepSevenStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepSevenStatus;
};

export const getStepEightStatus = (
  ongoingMonitoring?: OngoingMonitring[],
  ongoingCheckingStatus?: OngoingCheckingStatus,
  stepsStatus?: NewApplicationStepStatus,
) => {
  let stepEightStatus = NewApplicationStepStatus.AWAITING || stepsStatus;

  const ongoingWithWarnings: OngoingMonitring[] = [];

  if (ongoingMonitoring && ongoingMonitoring?.length > 0) {
    stepEightStatus = NewApplicationStepStatus.IN_PROGRESS;
  }

  ongoingMonitoring?.forEach((item) => {
    item?.warnings?.edges?.forEach((warning) => {
      if (!warning?.node?.action || warning?.node?.action === 'STOP') {
        ongoingWithWarnings.push(item);
      }
    });
  });

  if (ongoingMonitoring && !ongoingWithWarnings.length) {
    stepEightStatus = NewApplicationStepStatus.COMPLETED;
  } else if (ongoingCheckingStatus?.status === OngoingStatus.FAIL) {
    stepEightStatus = NewApplicationStepStatus.COMPLETED;
  }

  return stepEightStatus;
};

export const updateApplicationStepStatuses = ({
  values,
  stepsStatus,
  documents,
  agreements,
  mainRepresentativeApplications,
  companyOfficialsApplications,
  ongoingMonitoring,
  ongoingCheckingStatus,
  documentCategories,
  applicationScheme,
  questionnarieFields,
  accountsCurrenciesValues,
  expectedFundsOriginsValues,
  accountsFlowsValues,
}: {
  values: CorporateFormValues;
  stepsStatus?: NewApplicationCorporateSteps;
  documents?: ApplicationDocument[];
  agreements?: ApplicationAgreement[];
  mainRepresentativeApplications?: NewApplicationChildrenInfo[];
  companyOfficialsApplications?: NewApplicationChildrenInfo[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  documentCategories?: { id: string; required: boolean; category?: string }[];
  applicationScheme?: ApplicationScheme;
  questionnarieFields?: CorporateQuestionnarieFormValues;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  accountsFlowsValues?: AccountsFlowsFormValues[];
}): NewApplicationCorporateSteps => {
  let agreementsActive = null;
  let agreementsRequired = null;

  if (
    applicationScheme &&
    applicationScheme?.agreementsActive?.toString() !== 'null'
  ) {
    agreementsActive = applicationScheme?.agreementsActive;
  }

  if (
    applicationScheme &&
    applicationScheme?.agreementsRequired?.toString() !== 'null'
  ) {
    agreementsRequired = applicationScheme?.agreementsRequired;
  }

  const nextStepsStatus: NewApplicationCorporateSteps = {
    stepOne: getStepOneStatus(
      values,
      stepsStatus?.stepOne,
      applicationScheme,
      questionnarieFields,
      accountsCurrenciesValues,
      expectedFundsOriginsValues,
      accountsFlowsValues,
    ),
    stepTwo: getStepTwoStatus(values, stepsStatus?.stepTwo),
    stepThree: getStepThreeStatus(values, stepsStatus?.stepThree),
    stepFour: getStepFourStatus(
      mainRepresentativeApplications,
      stepsStatus?.stepFour,
    ),
    stepFive: getStepFiveStatus(
      companyOfficialsApplications,
      stepsStatus?.stepFive,
    ),
    stepSix: getStepSixStatus(
      documents,
      documentCategories,
      stepsStatus?.stepSix,
    ),
    stepSeven: getStepSevenStatus(
      agreements,
      stepsStatus?.stepSeven,
      agreementsActive,
      agreementsRequired,
    ),
    stepEight: getStepEightStatus(
      ongoingMonitoring,
      ongoingCheckingStatus,
      stepsStatus?.stepEight,
    ),
  };

  return nextStepsStatus;
};
